import { allProjects, getTimesheet, saveTimesheet, watchTimesheet, StaleTimesheet, usersById, listExpenses, listLeaveRequests, listInstructions, resumeIfHeld } from '../db.js';
import { escapeHtml, pill, avatarName, titleCase, refTail } from '../ui.js';
import { openOverlay } from './overlay.js';
// NOTE: openLogTime below is a bespoke day-grid over the week already open — a
// migration candidate for openPanel per the 7a sweep, but it is not a plain
// create/edit form (multi-day hour cells, add/set chips, per-row mileage/expense
// launchers), so left on openOverlay pending a designer decision (see
// DIALOGS&FORMS Q at openLogTime).
import { expenseForm } from './expense-form.js';
import { mileageBlock, footNote, mileageExpense } from './mileage-block.js';
import { myMileageAllowed } from '../my-currency.js';
import { addExpense } from '../db.js';
import { mileageRate } from '../org.js';
import { fmtMiles } from '../expense-maths.js';
import { isBankHoliday, isoOf } from '../leave.js';
import { sheetKey, ownerFields } from '../me.js';
import { expectedMinutes, contractLabel, userFor } from '../contract-hours.js';
import { orgSetting } from '../org.js';
import { IDLE_WEEKS, priorWeekStarts, priorEvidence, seedWeek, trimDraft, idleNotice, expenseKeysForWeek, rowKey, rowKeys, restoreRow } from '../timesheet-idle.js';

// Statuses whose projects appear on a timesheet by default (Karan 06/07):
// everything except Complete, i.e. work that could legitimately be logged to.
const SHEET_STATUSES = ['Not Started', 'In Progress', 'Review', 'Ready to Issue', 'Part Issued', 'Issued', 'On Hold'];
// The expected week is no longer built in (22/10/2026): the person's own figure,
// else the company's (ASP 37:30), else none (contract-hours.js) — and none when
// their timesheets are not required (users.timesheetExempt, 26/10/2026).
const weekContract = (users, session) => expectedMinutes(userFor(users, session), orgSetting('weeklyContractMinutes', null));

// Timesheet — weekly grid, now PERSISTED (first V2-native write path,
// 06/07/2026): one Firestore doc per user-week, auto-saved as you type,
// submitted for review with one click. Rows are projects you add (seeded from
// live projects the first time); each row can log mileage or a project
// expense straight into the expenses collection.

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const COLORS = ['var(--brand-blue)', 'var(--anchor)', 'var(--brand-orange)', '#C9A227', 'var(--hair-strong)'];
const STATUS_TONES = { draft: '', submitted: 'info', approved: 'ok', rejected: 'error' };
const PIN_SVG = '<svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false"><path d="M6 1.5h4M6.5 1.5v4.2L4 8.5h8L9.5 5.7V1.5M8 8.5V14.5" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const parse = (v) => {
  if (!v) return 0;
  const s = String(v).trim();
  if (s.includes(':')) { const [h, m] = s.split(':'); return (parseInt(h || 0, 10) * 60) + parseInt(m || 0, 10); }
  const f = parseFloat(s); return isNaN(f) ? 0 : Math.round(f * 60);
};
const fmt = (min) => (min ? Math.floor(min / 60) + ':' + String(min % 60).padStart(2, '0') : '');
const fmtBig = (min) => Math.floor(min / 60) + ':' + String(min % 60).padStart(2, '0');

// Hours are ALWAYS h:mm (Karan 25/08). A decimal point silently meant something
// else: "1.30" parsed as 1.3 hours — 1:18, not the 1:30 anyone typing it means.
// A full stop or comma now becomes the colon, and once three digits are in the
// field the colon places itself, so "130" reads 1:30 as you type.
function cleanTimeInput(v) {
  let s = String(v).replace(/[.,]/g, ':').replace(/[^\d:]/g, '');
  const i = s.indexOf(':');
  if (i !== -1) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/:/g, ''); // one colon only
  else if (s.length >= 3) s = s.slice(0, -2) + ':' + s.slice(-2);         // 130 → 1:30
  const [h, m] = s.split(':');
  return h.slice(0, 2) + (s.includes(':') ? ':' + (m || '').slice(0, 2) : '');
}
// Settle the field when it loses focus: a bare number is hours, a single minute
// digit is tens ("1:3" is 1:30 — quarter hours, not three minutes), minutes cap
// at 59. Whatever it becomes is on screen, so a misread is visible.
function normaliseTime(v) {
  const s = cleanTimeInput(String(v || '').trim());
  if (!s) return '';
  if (!s.includes(':')) {
    const n = parseInt(s, 10);
    return Number.isFinite(n) ? Math.min(n, 24) + ':00' : '';
  }
  const [hs, ms] = s.split(':');
  const h = Math.min(parseInt(hs || '0', 10) || 0, 24);
  const m = Math.min(parseInt((ms || '').padEnd(2, '0').slice(0, 2), 10) || 0, 59);
  return h + ':' + String(m).padStart(2, '0');
}
function bindTimeInput(inp) {
  inp.addEventListener('input', () => {
    const before = inp.value;
    const after = cleanTimeInput(before);
    if (after === before) return;
    const pos = inp.selectionStart;
    inp.value = after;
    const p = Math.max(0, (pos == null ? after.length : pos) + (after.length - before.length));
    try { inp.setSelectionRange(p, p); } catch (e) { /* not all inputs support it */ }
  });
  inp.addEventListener('blur', () => { inp.value = normaliseTime(inp.value); });
}

function mondayOf(d) {
  const m = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  m.setDate(m.getDate() - ((m.getDay() + 6) % 7));
  return m;
}
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function weekDays(monday) {
  const today = new Date();
  return DAY_KEYS.map((key, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return {
      key, date: d,
      label: d.toLocaleDateString('en-GB', { weekday: 'short' }),
      num: String(d.getDate()).padStart(2, '0'),
      today: isoDay(d) === isoDay(today),
      weekend: i >= 5,
    };
  });
}
/** "19 – 25 October 2026"; across a month "28 Sept – 4 Oct 2026" (p10). */
export function rangeLabel(monday) {
  const end = new Date(monday); end.setDate(monday.getDate() + 6);
  if (end.getMonth() === monday.getMonth()) {
    return `${monday.getDate()} – ${end.getDate()} ${end.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}`;
  }
  const short = { day: 'numeric', month: 'short' };
  return `${monday.toLocaleDateString('en-GB', short)} – ${end.toLocaleDateString('en-GB', { ...short, year: 'numeric' })}`;
}
function weekLabel(monday) {
  const end = new Date(monday); end.setDate(monday.getDate() + 6);
  const opts = { day: 'numeric', month: 'short' };
  return `${monday.toLocaleDateString('en-GB', opts)} – ${end.toLocaleDateString('en-GB', opts)} ${end.getFullYear()}`;
}

// A week locks the following Tuesday at 10:00 (Karan 23/07). After that a draft
// can't be edited or submitted until a Coordinator+ unlocks it.
function lockDeadline(monday) {
  const d = new Date(monday); d.setDate(d.getDate() + 8); d.setHours(10, 0, 0, 0); return d; // Mon+8 = the following Tue
}
const REVIEWER_ROLES = ['Owner', 'Director', 'Coordinator'];

// The open week's live listener. Module-level so re-entering the screen (or
// changing week) replaces it rather than stacking a second one.
let unwatch = null;

function loadingFrame(monday) {
  const isMobile = window.innerWidth <= 760;
  const head = isMobile ? `
      <div style="display:flex;align-items:flex-end;gap:16px;">
        <span style="font-size:52px;font-weight:500;line-height:0.86;letter-spacing:-0.04em;color:var(--hair-strong);">0:00</span>
        <div style="display:flex;flex-direction:column;gap:4px;padding-bottom:4px;">
          <span style="font-size:9.5px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;color:var(--brand-orange);">This week</span>
          <span style="font-size:12.5px;color:var(--ink-3);line-height:1.4;">Loading your week…</span>
        </div>
      </div>`
    : `
      <div class="wt-bar">
        <div class="wt-bar-l">
          <button type="button" class="btn wt-step" disabled aria-label="Previous week">‹</button>
          <span class="wt-range">${escapeHtml(rangeLabel(monday))}</span>
          <button type="button" class="btn wt-step" disabled aria-label="Next week">›</button>
        </div>
        <div class="wt-bar-r"><span class="wt-total"><b>0:00</b> loading your week…</span></div>
      </div>`;
  return `
      <div class="ts4" style="${isMobile ? 'padding:20px 16px 28px;display:flex;flex-direction:column;' : ''}" aria-busy="true">
        ${head}
      </div>`;
}

export async function renderTimesheet(mount, session) {
  if (unwatch) { unwatch(); unwatch = null; }
  // The India office claims no mileage unless settings.money.indiaMileage (Karan 01/11/2026).
  const mileageOn = await myMileageAllowed(session).catch(() => true);
  let monday = mondayOf(new Date());
  // The loading state draws the SAME frame as the loaded sheet (same padding,
  // eyebrow, 34px title, week line and a placeholder total), so nothing moves
  // when the week arrives (Karan 18/10/2026: the title used to jump).
  mount.innerHTML = loadingFrame(monday);

  // The saved sheet is ONE read — draw it immediately. The full project list
  // (for the Add-project picker / first-time seeding) loads in the background.
  let projects = [], live = [], mine = [], users = {};
  let projectsOk = false;   // the idle tidy-up drops nothing unless the projects were read
  const projectsReady = Promise.all([allProjects(), usersById().catch(() => ({}))]).then(([ps, us]) => {
    projects = ps;
    projectsOk = Array.isArray(ps);
    users = us;
    const me = sheetKey(session);
    live = ps.filter((p) => SHEET_STATUSES.includes(p.status));
    mine = live.filter((p) => refTail(p.lead) === me);
    // A personal figure (a part-timer) only shows once the people have loaded.
    if (sheet && userFor(users, session) && userFor(users, session).weeklyContractMinutes) draw();
  }).catch(() => {});

  const docId = () => `${sheetKey(session)}_${isoDay(monday)}`;
  let sheet = null; // current week's doc
  let saveTimer = null, statusEl = null, failedReason = '';
  // The rev this window is working from, and whether another window has moved
  // the doc on underneath us (Karan 27/08 — see saveTimesheet in db.js).
  let savedRev = 0, conflict = null;   // null | 'changed' | 'refused'

  // Mobile day-picker state (§1.9). Re-draw when we cross the phone breakpoint.
  let mobileDay = null;
  let lastMobile = window.innerWidth <= 760;
  const onResize = () => {
    if (!mount.isConnected) { window.removeEventListener('resize', onResize); return; }
    const m = window.innerWidth <= 760;
    if (m !== lastMobile) { lastMobile = m; if (sheet) draw(); }
  };
  window.addEventListener('resize', onResize);

  // Approved leave for THIS user (Karan 23/07) — bank holidays + these blank out the
  // day and cut the week's expected hours. Loads in the background; redraws when ready.
  let myLeave = [];
  listLeaveRequests().then((rs) => {
    myLeave = (rs || []).filter((r) => r.userEmail === session.email && r.status === 'approved');
    if (sheet) draw();
  }).catch(() => {});

  // Projects the user removed stay removed on future weeks' seeding (Karan
  // 13/07). Per-user, kept locally; adding a project back un-hides it.
  const hiddenKey = 'track-ts-hidden-' + session.email;
  let hidden = new Set();
  try { hidden = new Set(JSON.parse(localStorage.getItem(hiddenKey) || '[]')); } catch (e) { /* fresh */ }
  const saveHidden = () => { try { localStorage.setItem(hiddenKey, JSON.stringify([...hidden])); } catch (e) { /* ignore */ } };

  // PINS — "Keep on my timesheet" (Karan 26/10/2026): a pinned project (or
  // practice-time category) is never taken off for having no time. Kept where
  // the hidden list lives — this browser, per person — because no Firestore
  // rule lets a person write their own preferences (users/{id} allows only
  // helpSeen), and the rules are not widened for this. Keys are rowKey()s.
  const pinKey = 'track-ts-pinned-' + session.email;
  let pins = new Set();
  try { pins = new Set(JSON.parse(localStorage.getItem(pinKey) || '[]')); } catch (e) { /* fresh */ }
  const savePins = () => { try { localStorage.setItem(pinKey, JSON.stringify([...pins])); } catch (e) { /* ignore */ } };
  const isPinned = (r) => rowKeys(r).some((k) => pins.has(k));
  const setPinned = (r, on) => {
    const k = rowKey(r); if (!k) return;
    if (on) { pins.add(k); if (r.projectId) { hidden.delete(r.projectId); saveHidden(); } }
    else rowKeys(r).forEach((x) => pins.delete(x));
    savePins();
  };
  // The idle notice is shown until dismissed (per week, per browser); a new
  // removal later in the week brings it back.
  const noticeKey = () => 'track-ts-idle-seen-' + docId();

  // Projects with mileage/expenses by this user → a notifier icon on the row
  // (Karan issue 13/07). Loaded in the background; re-draws once known.
  const expenseFlags = {}; // projectId → { mileage, expense }
  let myExpenses = null;   // null until read; the idle tidy-up drops nothing without them
  const expensesReady = listExpenses({ userEmail: session.email }).then((xs) => {
    myExpenses = Array.isArray(xs) ? xs : null;
    (xs || []).forEach((x) => {
      if (!x.projectId) return;
      const f = expenseFlags[x.projectId] || (expenseFlags[x.projectId] = {});
      if (x.type === 'Mileage') f.mileage = true; else f.expense = true;
    });
    if (sheet) draw();
  }).catch(() => {});

  // Internal ASP categories (Admin, Sick, Training, Meeting, Holiday…) USED to be
  // pseudo-projects numbered 0–5 (client "ASP") so there was somewhere to log the
  // hours. Those projects are retired (Karan 24/08): holiday, sick and bank
  // holidays are handled by the leave module (they blank the day below), and
  // the remaining categories are first-class "ASP time" rows with no project —
  // `internal` names the category, projectId is null, the status engine and
  // the project rollups ignore them. isInternal() stays only to keep rows that
  // were already logged against the old pseudo-projects reading "Admin", not a
  // number (Sanj 17/07), and to keep those projects out of the search pool.
  const ASP_CATEGORIES = [
    { key: 'admin', label: 'Admin' }, { key: 'marketing', label: 'Marketing' }, { key: 'training', label: 'Training' },
    { key: 'meeting', label: 'Meeting' }, { key: 'cpd', label: 'CPD' },
  ];
  const INTERNAL_SERVICES = ['Admin', 'Sick', 'Marketing', 'Bank Holiday', 'Training', 'Meeting', 'Holiday', 'CPD', 'Leave', 'Annual Leave'];
  const isInternal = (p) => INTERNAL_SERVICES.some((n) => n.toLowerCase() === String(p.service || p.clientName || p.title || '').toLowerCase());
  const internalRow = (cat) => ({
    projectId: null, internal: cat.key, number: 'ASP', label: cat.label, task: 'ASP time',
    instructionSeq: null, instructionDesc: null, days: {}, note: '', review: false,
  });
  // The category an existing row represents — a new ASP-time row, or a legacy
  // row on a retired pseudo-project whose label is the category name.
  const rowCategory = (r) => r.internal || (ASP_CATEGORIES.find((c) => c.label.toLowerCase() === String(r.label || '').toLowerCase()) || {}).key || null;
  // Rows saved BEFORE the pseudo-projects were retired still carry their old
  // number, so a week in progress read "5 Training", "0 Admin", "2 Marketing"
  // (Karan 25/08). They are ASP time whichever way they were created — show
  // them as such. Display only: the stored row is left alone, so an approved
  // week still says exactly what was submitted.
  const isAspTime = (r) => !!r.internal || r.task === 'ASP time';
  const rowNumber = (r) => (isAspTime(r) ? 'ASP' : (r.number || '—'));
  const newRow = (p, instr) => ({
    projectId: p.id || null, number: p.number || '—',
    label: (isInternal(p) ? (p.service || p.clientName || p.title) : (p.clientName || p.title)) || p.service || 'Project',
    task: isInternal(p) ? 'ASP time' : (p.service || p.status || 'Time'),
    // Golden thread (Karan 23/07): which instruction this time is for. Defaults to
    // #1; a picker appears when the project has more than one.
    instructionSeq: instr && instr.seq ? Number(instr.seq) : 1,
    instructionDesc: (instr && Number(instr.seq) !== 1 && instr.description) ? String(instr.description).slice(0, 60) : null,
    days: {}, note: '', review: false,
  });

  /** The projects a new week starts with: yours if you lead any, else the
   *  first few live ones — minus anything you have removed before, unless
   *  removing leaves nothing, in which case a populated week beats a blank one. */
  function seedRows() {
    const preferred = mine.length ? mine : live.slice(0, 4);
    const kept = preferred.filter((p) => !hidden.has(p.id));
    return kept.length ? kept : preferred;
  }

  // ── Idle rows (Karan 26/10/2026; rule + tests in timesheet-idle.js) ─────
  // The IDLE_WEEKS weeks before the open one: three reads by id. A read that
  // FAILS is kept as a failure, never as "no sheet", so the tidy-up then
  // drops nothing that week.
  const sheetIdFor = (weekStart) => `${sheetKey(session)}_${weekStart}`;
  const readPriors = (weekStart) => Promise.all(priorWeekStarts(weekStart)
    .map((ws) => getTimesheet(sheetIdFor(ws)).then((sh) => ({ sheet: sh }), () => ({ failed: true }))));
  async function idleContext(weekStart) {
    const [priors] = await Promise.all([readPriors(weekStart), projectsReady, expensesReady]);
    return {
      priors,
      ctx: {
        evidence: priorEvidence(priors), pins, weekStart,
        projectsById: Object.fromEntries(projects.map((p) => [p.id, p])), projectsOk,
        expensesOk: !!myExpenses, expenseKeys: myExpenses ? expenseKeysForWeek(myExpenses, weekStart) : null,
      },
    };
  }
  // Rows for everything pinned that is not otherwise on a new week.
  function pinnedRows() {
    const out = [];
    for (const k of pins) {
      if (k.startsWith('asp:')) { const c = ASP_CATEGORIES.find((x) => 'asp:' + x.key === k); if (c) out.push(internalRow(c)); continue; }
      const p = k.startsWith('p:') ? live.find((x) => 'p:' + x.id === k) : live.find((x) => 'n:' + String(x.number) === k);
      if (p && !isInternal(p)) out.push(newRow(p));
    }
    return out;
  }

  /**
   * The open week, already saved as a draft: rows with nothing this week and
   * no time in the IDLE_WEEKS before come off. Runs after the week is on
   * screen and saves through the rev guard; if the week moved on meanwhile
   * (another window, or you started typing) it does nothing — it never
   * overwrites anyone's edit, it just tries again next time the week opens.
   */
  async function tidyWeek() {
    const id = docId(), at = sheet, rev = savedRev, ws = isoDay(monday);
    if (!at || !at.id || at.status !== 'draft') return;
    let ic;
    try { ic = await idleContext(ws); } catch (e) { return; }
    if (id !== docId() || sheet !== at || savedRev !== rev || conflict || dirty()) return;
    const locked = new Date() >= lockDeadline(monday);
    const t = trimDraft(at, ic.ctx, { locked });
    if (!t) return;
    const next = { ...at, rows: t.rows, idleRemoved: t.idleRemoved, updatedAt: new Date().toISOString() };
    try {
      const nrev = await saveTimesheet(id, next, rev);
      if (id !== docId() || sheet !== at) return;
      sheet = next; savedRev = nrev;
      draw();
    } catch (e) { /* refused (moved on) or failed: nothing written, nothing lost — leave the week as it is */ }
  }

  // The quiet line above the table: what came off, Undo, dismiss.
  function idleShown() {
    const onSheet = new Set(sheet.rows.flatMap(rowKeys));
    return (sheet.idleRemoved || []).filter((x) => x && x.key && !onSheet.has(x.key));
  }
  function idleLine() {
    const list = idleShown();
    if (!list.length) return '';
    let seenSig = null;
    try { seenSig = localStorage.getItem(noticeKey()); } catch (e) { /* show it */ }
    if (seenSig === list.map((x) => x.key).join(',')) return '';
    return `<p class="wt-note ts-idle" id="ts-idle"><span>${escapeHtml(idleNotice(list))}</span>
      <button type="button" class="linkbtn" id="ts-idle-undo">Undo</button>
      <button type="button" class="linkbtn" id="ts-idle-x" aria-label="Dismiss">Dismiss</button></p>`;
  }

  async function loadWeek() {
    // A FAILED READ IS NOT AN EMPTY WEEK. This swallowed the error and seeded a
    // blank sheet, so a permission failure looked exactly like "you have not
    // started this week" — and the hours already stored were simply not shown.
    // That is how a week appears to have vanished when it is sitting there.
    let readFailed = null;
    sheet = await getTimesheet(docId()).catch((e) => { readFailed = e; return null; });
    if (readFailed) {
      conflict = 'failed';
      failedReason = `this week could not be loaded (${readFailed.message || readFailed})`;
      sheet = { userEmail: session.email, ...ownerFields(session), userName: session.displayName,
                weekStart: isoDay(monday), status: 'draft', rows: [] };
      savedRev = 0;
      draw();
      paintConflict();
      return;
    }
    if (!sheet) {
      // A new week: carry forward what you logged time to in the last
      // IDLE_WEEKS weeks, add your lead projects and your pins, then leave off
      // anything idle (timesheet-idle.js) — unless a read failed, when nothing is left off.
      const weekAt = isoDay(monday);
      const ic = await idleContext(weekAt).catch(() => null);
      if (weekAt !== isoDay(monday)) return;   // the week changed while we read
      const liveIds = new Set(live.map((p) => p.id));
      const seed = ic
        ? seedWeek({ priors: ic.priors, seeded: seedRows().map((p) => newRow(p)), pinned: pinnedRows(),
            isLive: (pid) => liveIds.has(pid), hidden }, ic.ctx)
        : { rows: seedRows().map((p) => newRow(p)), removed: [] };
      sheet = {
        userEmail: session.email, ...ownerFields(session), userName: session.displayName,
        weekStart: isoDay(monday), status: 'draft',
        // REMOVING A ROW HIDES THAT PROJECT FROM FUTURE WEEKS (localStorage,
        // per person, per browser). That is a convenience, not an instruction
        // to hand somebody a blank week — and this filtered AFTER choosing the
        // list, so hiding every seeded project left rows: []. An empty sheet
        // has nothing to type into, a sheet is only created on its first save,
        // and so the week never existed at all. That is how a week goes
        // missing entirely rather than merely coming up short.
        rows: seed.rows,
        ...(seed.removed.length ? { idleRemoved: seed.removed } : {}),
      };
    }
    savedRev = Number(sheet.rev || 0);
    conflict = null;
    watchWeek();
    draw();
    tidyWeek();
  }

  // Unsaved work: a save is queued, or the cursor is in a field the user is
  // part-way through. Either way we must not swap the sheet under them.
  function dirty() {
    if (saveTimer) return true;
    const a = document.activeElement;
    return !!(a && mount.contains(a) && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA'));
  }

  // The open week, live. A change that is not ours either lands silently or,
  // if there is unsaved work on screen, raises the bar rather than discarding it.
  function watchWeek() {
    if (unwatch) { unwatch(); unwatch = null; }
    const id = docId();
    unwatch = watchTimesheet(id, (remote) => {
      if (!document.contains(mount)) { if (unwatch) { unwatch(); unwatch = null; } return; }
      if (!remote || id !== docId()) return;                 // week changed under us
      const rrev = Number(remote.rev || 0);
      if (rrev <= savedRev) return;                          // our own write, or nothing new
      if (dirty()) { conflict = conflict || 'changed'; paintConflict(); return; }
      sheet = remote;
      savedRev = rrev;
      draw();
      if (statusEl) statusEl.textContent = 'Updated from another window';
    });
  }

  // The conflict bar. draw() rebuilds mount wholesale, so this re-inserts
  // itself at the end of every draw rather than living in the template.
  function paintConflict() {
    const existing = mount.querySelector('#ts-conflict');
    if (!conflict) { if (existing) existing.remove(); return; }
    // Say only what is true: a refused save really did not write, whereas a
    // change noticed while you were mid-edit has not cost you anything yet.
    const msg = conflict === 'failed'
      ? `${failedReason.startsWith('this week') ? 'Nothing has been lost, but ' + failedReason
          : 'Your hours have NOT been saved — ' + failedReason}. `
        + 'Do not re-type your hours until this is cleared; reload to try again.'
      : conflict === 'refused'
        ? 'This week was changed in another window, so your last change was not saved. '
          + 'Nothing has been overwritten.'
        : 'This week was changed in another window. What is on screen here is out of date.';
    if (existing) { existing.querySelector('span').textContent = msg; return; }
    const bar = document.createElement('div');
    bar.id = 'ts-conflict';
    bar.className = 'ts-conflict';
    bar.innerHTML = conflict === 'failed'
      ? '<span></span><button type="button" id="ts-conflict-retry">Try saving again</button>'
      : '<span></span><button type="button" id="ts-conflict-reload">Reload the week</button>';
    bar.querySelector('span').textContent = msg;
    bar.querySelector('#ts-conflict-retry')?.addEventListener('click', async () => {
      if (await saveNow()) { conflict = null; failedReason = ''; paintConflict();
        if (statusEl) statusEl.textContent = 'Saved'; }
    });
    bar.querySelector('#ts-conflict-reload')?.addEventListener('click', async () => {
      clearTimeout(saveTimer); saveTimer = null;   // the queued stale save is abandoned
      conflict = null;
      await loadWeek();
    });
    mount.insertBefore(bar, mount.firstChild);
  }

  function scheduleSave() {
    if (statusEl) statusEl.textContent = 'Saving…';
    clearTimeout(saveTimer);
    saveTimer = setTimeout(async () => {
      try {
        sheet.updatedAt = new Date().toISOString();
        savedRev = await saveTimesheet(docId(), sheet, savedRev);
        saveTimer = null;
        if (statusEl) statusEl.textContent = 'Saved ' + new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
        // Time on a held project takes it OFF hold by itself (Karan 11/08).
        [...new Set(sheet.rows.filter((r) => Object.values(r.days || {}).some(Boolean)).map((r) => r.projectId).filter(Boolean))]
          .forEach((pid) => resumeIfHeld(pid, session.email, session.displayName, 'time was logged').catch(() => {}));
      } catch (e) {
        saveTimer = null;
        if (e instanceof StaleTimesheet || e.name === 'StaleTimesheet') {
          // Refused, not failed: the other window's hours are intact and so is
          // what is on screen here. The bar is the only honest thing to show.
          conflict = 'refused';
          paintConflict();
          if (statusEl) statusEl.textContent = 'Not saved — this week changed elsewhere';
        } else {
          // A status line is wiped by the next draw(), so a failed save left no
          // trace and the hours on screen looked saved. The bar survives a
          // redraw and says plainly that nothing was written.
          conflict = 'failed';
          failedReason = e.message || String(e);
          paintConflict();
          if (statusEl) statusEl.textContent = 'NOT SAVED';
        }
      }
    }, 800);
  }

  /**
   * The button paths — submit, recall, request unlock, request weekend — write
   * the whole sheet too, so they carry the same guard. Returns true when the
   * write landed; false means it was refused and the bar is up.
   */
  async function saveNow() {
    try {
      savedRev = await saveTimesheet(docId(), sheet, savedRev);
      return true;
    } catch (e) {
      if (e instanceof StaleTimesheet || e.name === 'StaleTimesheet') {
        conflict = 'refused';
        paintConflict();
        if (statusEl) statusEl.textContent = 'Not saved — this week changed elsewhere';
        return false;
      }
      throw e;
    }
  }

  // Pick which instruction a project's time is for (only when it has >1). Resolves
  // the chosen instruction, or null if the picker was closed without choosing.
  // DIALOGS&FORMS Q: single-choice row-picker (choose-one) — no editable fields.
  // Left on openOverlay per the surface test (7a); inline-select is its future home.
  function pickInstruction(instrs) {
    return new Promise((resolve) => {
      const { content, close, onClose } = openOverlay({ title: 'Which instruction?', subtitle: 'This project has more than one — pick the one you worked on' });
      let picked = false;
      content.innerHTML = `<div style="padding:12px 16px 16px">${instrs.map((i, n) => `
        <div class="ct-row pk-instr" style="grid-template-columns:44px 1fr;padding:11px 12px;cursor:pointer" data-n="${n}">
          <span class="mono strong">#${escapeHtml(String(i.seq))}</span>
          <span>${escapeHtml(i.description || 'Instruction')}${i.status ? ` <span class="sec">· ${escapeHtml(i.status)}</span>` : ''}</span>
        </div>`).join('')}</div>`;
      content.querySelectorAll('.pk-instr').forEach((r) => r.addEventListener('click', () => { picked = true; const i = instrs[+r.dataset.n]; close(); resolve(i); }));
      if (onClose) onClose(() => { if (!picked) resolve(null); });
    });
  }

  // DIALOGS&FORMS Q: row-picker (add-to-timesheet) — search then click a project
  // row to add it to the sheet; no editable form fields. Left on openOverlay per
  // the surface test (7a); inline-select is its future home.
  async function addProjectPicker() {
    const { content, close } = openOverlay({ title: 'Add project', subtitle: 'Yours first, then all live projects — or search everything' });
    content.innerHTML = `<div style="padding:14px 18px 18px">
      <div id="pk-asp" style="margin-bottom:14px"></div>
      <input class="f-input" id="pk-q" placeholder="Search number, client, description…" autocomplete="off">
      <div id="pk-list" style="margin-top:10px;max-height:calc(52 * var(--vh));overflow-y:auto"><p class="g-note">Loading projects…</p></div></div>`;
    await projectsReady;
    const listEl = content.querySelector('#pk-list');
    const qEl = content.querySelector('#pk-q');
    const onSheet = new Set(sheet.rows.map((r) => r.projectId).filter(Boolean));

    // ASP time — one-click add for the internal categories (Admin, Marketing,
    // Training, Meeting, CPD), which are rows in their own right, not projects
    // (Karan 27/07 quick-add; project-free since 24/08). Holiday and sick are
    // not here: approved leave already blanks the day.
    const aspEl = content.querySelector('#pk-asp');
    const onSheetCats = new Set(sheet.rows.map(rowCategory).filter(Boolean));
    const aspCats = ASP_CATEGORIES.filter((c) => !onSheetCats.has(c.key));
    if (aspCats.length) {
      aspEl.innerHTML = `<div class="eyebrow blue" style="margin-bottom:8px">Practice time</div>
        <div style="display:flex;flex-wrap:wrap;gap:8px">${aspCats.map((c, i) => `<button class="btn ghost pk-asp-btn" data-i="${i}" style="padding:6px 14px;font-size:13px">${escapeHtml(c.label)}</button>`).join('')}</div>`;
      aspEl.querySelectorAll('.pk-asp-btn').forEach((b) => b.addEventListener('click', () => {
        const c = aspCats[+b.dataset.i]; if (!c) return;
        sheet.rows.push({ ...internalRow(c), added: true }); close(); draw(); scheduleSave();
      }));
    }
    const drawList = () => {
      const q = qEl.value.trim().toLowerCase();
      // Retired pseudo-projects never come back through search — ASP time is the strip above.
      const pool = (q ? projects.filter((p) => !isInternal(p) && [p.number, p.clientName, p.title].some((v) => String(v || '').toLowerCase().includes(q)))
        : [...mine, ...live.filter((p) => !mine.includes(p))]).filter((p) => !isInternal(p) && !onSheet.has(p.id));
      listEl.innerHTML = pool.slice(0, 30).map((p, i) => {
        const lead = refTail(p.lead);
        const leadLabel = lead ? titleCase((users[lead] && users[lead].displayName) || lead) : '';
        return `
        <div class="ct-row" style="grid-template-columns:64px 1fr auto;padding:10px 12px" data-i="${i}">
          <span class="mono strong">${escapeHtml(p.number || '—')}</span>
          <span style="min-width:0">
            <span class="strong" style="display:block;overflow:hidden;text-overflow:ellipsis">${escapeHtml((isInternal(p) ? (p.service || p.clientName || p.title) : (p.clientName || p.title)) || p.service || '')}</span>
            <span class="sec" style="font-size:12px">${avatarName(leadLabel, { size: 18 })}</span>
          </span>
          <span>${pill(p.status || '—', ['In Progress','Review'].includes(p.status) ? 'info' : p.status === 'Not Started' ? '' : 'warn')}</span>
        </div>`;
      }).join('') || '<p class="g-note">No matches.</p>';
      listEl.querySelectorAll('.ct-row').forEach((r) => r.addEventListener('click', async () => {
        const p = pool[+r.dataset.i];
        if (!p) return;
        if (p.id && hidden.has(p.id)) { hidden.delete(p.id); saveHidden(); } // added back → no longer hidden
        // Golden thread: attach the row to an instruction. Real projects with more
        // than one instruction ask which; single-instruction (and internal) rows use #1.
        let instr = null;
        if (p.id && !isInternal(p)) {
          const instrs = await listInstructions(p.id).catch(() => []);
          if (instrs.length > 1) { instr = await pickInstruction(instrs); if (!instr) return; }
          else if (instrs.length === 1) instr = instrs[0];
        }
        // Added by hand: kept this week even before any time goes on it.
        sheet.rows.push({ ...newRow(p, instr), added: true });
        close(); draw(); scheduleSave();
      }));
    };
    qEl.addEventListener('input', drawList);
    qEl.focus();
    drawList();
  }

  function draw() {
    const days = weekDays(monday);
    // Weekend (Sat/Sun) columns are hidden by default; they only appear once a
    // reviewer has approved weekend working for this week (Karan 13/07).
    const weekendOpen = sheet.weekendApproved === true;
    const vdays = weekendOpen ? days : days.filter((d) => !d.weekend);
    const deadline = lockDeadline(monday);
    const pastDeadline = new Date() >= deadline;
    // Past the Tuesday-10am deadline, an un-submitted week locks until a reviewer unlocks it.
    const timeLocked = pastDeadline && !sheet.unlocked && (sheet.status === 'draft' || sheet.status === 'rejected');
    const locked = sheet.status === 'submitted' || sheet.status === 'approved' || timeLocked;
    const lockDateLbl = deadline.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    const weekendCtl = weekendOpen
      ? '<span class="wt-save">Weekend open</span>'
      : sheet.weekendRequested
        ? '<span class="wt-save">Weekend requested — awaiting approval</span>'
        : (sheet.status === 'draft' || sheet.status === 'rejected')
          ? '<button type="button" class="linkbtn wt-now" id="req-weekend" title="Ask a reviewer to open Saturday and Sunday for this week">Request weekend</button>'
          : '';

    // Per-day off status (bank holiday / approved leave) + the week's EXPECTED hours
    // (7.5h contract day, halved for a half-day), which drops as those days land.
    const contractMin = weekContract(users, session);   // null: no expected week
    const DAY_MIN = (contractMin || 0) / 5; // ASP: 450 = 7:30 contract day
    const dayInfo = {};
    let expectedMin = 0;
    vdays.forEach((d) => {
      if (d.weekend) { dayInfo[d.key] = null; return; }
      const iso = isoOf(d.date);
      if (isBankHoliday(iso)) { dayInfo[d.key] = { label: 'Bank holiday', tone: 'bh' }; return; }
      const lv = myLeave.find((r) => r.from && r.from <= iso && (r.to || r.from) >= iso);
      if (lv) {
        const half = lv.halfDay && lv.from === (lv.to || lv.from);
        dayInfo[d.key] = { label: (lv.kind === 'sick' ? 'Sick' : 'Annual leave') + (half ? ' ½' : ''), tone: lv.kind === 'sick' ? 'sick' : 'leave' };
        expectedMin += half ? DAY_MIN / 2 : 0;
        return;
      }
      dayInfo[d.key] = null;
      expectedMin += DAY_MIN;
    });

    // §1.9 mobile: the timesheet inverts — pick a day, see that day's rows —
    // rather than scrolling a week grid nobody can read on a phone.
    const isMobile = window.innerWidth <= 760;
    if (!mobileDay || !vdays.some((d) => d.key === mobileDay)) mobileDay = (vdays.find((d) => d.today) || vdays[0] || { key: 'mon' }).key;
    const dayTot = {};
    DAY_KEYS.forEach((k) => { dayTot[k] = sheet.rows.reduce((t, r) => t + parse(r.days && r.days[k]), 0); });

    function desktopGrid() {
      // v4 (p10): a table — project · instruction, one column per visible day,
      // Total, ×. Today's cells sit in a faint box (they read as inputs); leave
      // and bank-holiday cells are hatched. Each row leads with a dot: project
      // time, review time, practice time.
      const head = `<tr>
          <th>Project · instruction</th>
          ${vdays.map((d) => { const off = dayInfo[d.key];
            return `<th class="c ts4-dh${d.today ? ' today' : ''}">${escapeHtml(d.label)} ${escapeHtml(String(Number(d.num)))}${
              d.today ? '<span>today</span>' : off ? `<span>${escapeHtml(off.label.toLowerCase())}</span>` : ''}</th>`; }).join('')}
          <th class="r">Total</th><th aria-label="Pin or remove"></th>
        </tr>`;

      const rows = sheet.rows.map((r, ri) => {
        const asp = isAspTime(r);
        const flags = expenseFlags[r.projectId] || {};
        const seq = Number(r.instructionSeq) || 0;
        const sub = [
          asp ? 'Practice time' : (r.task || ''),
          !asp && r.instructionDesc ? `#${r.instructionSeq} ${r.instructionDesc}` : (!asp && seq ? `I${seq}` : ''),
          r.review ? 'review time' : '',
          isPinned(r) ? 'pinned' : '',
          flags.mileage ? 'mileage logged' : '',
          flags.expense ? 'expense logged' : '',
          r.note || (r.dayNotes && Object.keys(r.dayNotes).length) ? 'note' : '',
        ].filter(Boolean).join(' · ');
        const kind = asp ? 'practice' : r.review ? 'review' : 'project';
        const cells = vdays.map((d) => {
          const val = (r.days && r.days[d.key]) || '';
          const dn = r.dayNotes && r.dayNotes[d.key];
          const off = dayInfo[d.key];
          const cls = ['c', 'num', 'ts4-cell', d.today ? 'today' : '', off ? 'off' : '', val ? '' : 'dim', locked ? '' : 'live'].filter(Boolean).join(' ');
          return `<td class="${cls}"${dn ? ` title="${escapeHtml(dn)}"` : ''}${locked ? '' : ` data-open="${ri}" data-day="${d.key}" role="button" tabindex="0" aria-label="${escapeHtml(`Log ${d.label} ${d.num} on ${asp ? r.label : rowNumber(r)}`)}"`}><span>${val ? escapeHtml(fmt(parse(val)) || val) : (off ? '' : '–')}</span></td>`;
        }).join('');
        return `<tr>
            <td><div class="ts4-proj">
              <span class="ts4-dot ${kind}" aria-hidden="true"></span>
              <span class="ts4-pm${locked ? '' : ' live'}"${locked ? '' : ` data-openweek="${ri}" role="button" tabindex="0" title="Log time for the whole week"`}>
                <span class="ts4-pn">${asp ? '' : `<b class="num-tech">${escapeHtml(rowNumber(r))}</b> `}${escapeHtml(r.label)}</span>
                <span class="ts4-ps">${escapeHtml(sub)}</span>
              </span>
            </div></td>
            ${cells}
            <td class="r num ts4-rt" id="rowtot-${ri}">0:00</td>
            <td class="ts4-x">${locked ? '' : `<button type="button" class="ts4-pin${isPinned(r) ? ' on' : ''}" data-pin="${ri}" aria-pressed="${isPinned(r) ? 'true' : 'false'}" title="${isPinned(r) ? 'Pinned: never taken off for having no time. Click to unpin' : 'Keep on my timesheet: never taken off for having no time'}" aria-label="${isPinned(r) ? 'Unpin row' : 'Keep on my timesheet'}">${PIN_SVG}</button><button type="button" data-rm="${ri}" title="Remove row" aria-label="Remove row">×</button>`}</td>
          </tr>`;
      }).join('');

      const empty = sheet.rows.length ? '' : `<tr><td colspan="${vdays.length + 3}" class="dim">No rows — add a project to start logging.</td></tr>`;

      const leaveDays = vdays.filter((d) => dayInfo[d.key]);
      const foot = `Click a cell to log that day; click a project to log the whole week. Notes, mileage and expenses sit with the entry.${
        leaveDays.length && contractMin ? ` ${leaveDays.map((d) => d.date.toLocaleDateString('en-GB', { weekday: 'long' })).join(' and ')} ${leaveDays.length === 1 ? 'is' : 'are'} ${leaveDays.length === 1 ? dayInfo[leaveDays[0].key].label.toLowerCase() : 'off'}, so the week expects ${fmtBig(expectedMin)}.` : ''}`;

      return `<div class="wt-scroll"><table class="wt-table ts4-grid">
          <thead>${head}</thead>
          <tbody class="arrive">${rows}${empty}</tbody>
          <tfoot><tr>
            <td class="lbl">Day total</td>
            ${vdays.map((d) => `<td class="c ts4-dt${d.today ? ' today' : ''}" id="daytot-${d.key}">0:00</td>`).join('')}
            <td class="r grand" id="weekgrand">0:00</td><td></td>
          </tr></tfoot>
        </table></div>
        <p class="wt-foot">${escapeHtml(foot)}</p>`;
    }
    function mobileGrid() {
      // §3c: the phone inverts the grid — a day picker, then that day's rows.
      const selOff = dayInfo[mobileDay];
      const sel = vdays.find((d) => d.key === mobileDay) || vdays[0];
      const selTot = dayTot[mobileDay] || 0;
      const dowCaps = 'font-size:9.5px;font-weight:500;letter-spacing:0.1em;text-transform:uppercase;';

      const picker = `
        <div style="display:flex;gap:5px;margin-top:16px;">
          ${vdays.map((d) => { const off = dayInfo[d.key]; const t = dayTot[d.key]; const active = d.key === mobileDay;
            const bg = active ? 'background:var(--ink-fill);color:var(--ink-fill-ink);' : d.today ? 'background:var(--brief-bg);' : '';
            return `<button type="button" data-mday="${d.key}" style="flex:1;display:flex;flex-direction:column;align-items:center;gap:5px;padding:10px 0;border-radius:10px;border:none;cursor:pointer;${bg}">
              <span style="${dowCaps}${active ? '' : 'color:var(--ink-3);'}">${escapeHtml(d.label)}</span>
              <span style="font-family:var(--font-technical);font-size:13px;font-weight:500;">${escapeHtml(d.num)}</span>
              <span style="font-family:var(--font-technical);font-size:11px;${active ? 'opacity:0.75;' : t ? '' : 'color:var(--ink-3);'}">${off && !t ? '–' : t ? escapeHtml(fmt(t)) : '–'}</span>
            </button>`; }).join('')}
        </div>`;

      const selLabel = `
        <div style="margin-top:22px;display:flex;align-items:baseline;gap:10px;">
          <span style="font-size:9.5px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;color:var(--link);">${escapeHtml(sel.date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }))}</span>
          <span style="flex:1;"></span>
          <span style="font-family:var(--font-technical);font-size:12px;color:var(--ink-3);">${selOff ? escapeHtml(selOff.label) + ' — no hours expected' : (selTot ? escapeHtml(fmt(selTot)) + ' logged' : 'nothing logged')}</span>
        </div>`;

      const rows = sheet.rows.map((r, ri) => {
        const val = (r.days && r.days[mobileDay]) || '';
        const sub = r.instructionDesc
          ? `#${escapeHtml(String(r.instructionSeq))} ${escapeHtml(r.instructionDesc)}`
          : escapeHtml(r.task || '')
            + (r.review ? ` · <span style="color:var(--link);font-weight:500;">Review time</span>` : '')
            + (r.note ? ` · <span style="color:var(--ink-3);">note</span>` : '');
        return `<div style="display:flex;align-items:center;gap:12px;padding:14px 0;border-bottom:1px solid var(--hair);${locked ? '' : 'cursor:pointer;'}"${locked ? '' : ` data-open="${ri}" data-day="${mobileDay}" role="button" tabindex="0"`}>
            <span style="width:7px;height:7px;border-radius:50%;background:${COLORS[ri % COLORS.length]};flex:0 0 7px;"></span>
            <span style="min-width:0;flex:1;display:flex;flex-direction:column;gap:3px;">
              <span style="font-size:14px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;"><span style="font-family:var(--font-technical);font-weight:500;">${escapeHtml(rowNumber(r))}</span> &nbsp;${escapeHtml(r.label)}</span>
              <span style="font-size:11.5px;color:var(--ink-3);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${sub}</span>
            </span>
            <span style="font-family:var(--font-technical);font-size:17px;font-weight:500;${val ? '' : 'color:var(--ink-3);'}">${val ? escapeHtml(fmt(parse(val)) || val) : '–'}</span>
          </div>`; }).join('') || '<div style="padding:16px 2px;font-size:12.5px;color:var(--ink-3);">No rows — add a project to start logging.</div>';

      const addBlock = locked ? '' : `
        <div style="margin-top:18px;display:flex;flex-direction:column;gap:10px;">
          <button id="add-proj" style="align-self:flex-start;background:none;border:none;padding:0;font-size:12.5px;color:var(--link);cursor:pointer;">+ Add a project to this day</button>
          <span style="font-size:12px;color:var(--ink-3);">Admin · Marketing · Training · Meeting · CPD</span>
        </div>`;

      const footer = locked ? '' : `
        <div style="margin-top:18px;border-top:1px solid var(--hair);padding-top:12px;display:flex;gap:10px;align-items:center;">
          ${mileageOn ? '<button id="ts-m-mileage" style="flex:1;border:1px solid var(--hair);border-radius:8px;padding:13px 0;font-size:13px;text-align:center;background:none;color:var(--ink);cursor:pointer;">Mileage</button>' : ''}
          <button id="ts-m-log" style="flex:1;background:var(--ink-fill);color:var(--ink-fill-ink);border:none;border-radius:8px;padding:13px 0;font-size:13px;font-weight:500;text-align:center;cursor:pointer;">Log time</button>
        </div>`;

      return `<div>${picker}${selLabel}<div style="margin-top:6px;">${rows}</div>${addBlock}${footer}</div>`;
    }

    // Week total + contract gap (both derived from the same day totals recalc()
    // recomputes; the sub-line follows because a day edit re-draws the sheet).
    const weekMin = DAY_KEYS.reduce((t, k) => t + dayTot[k], 0);
    const shortMin = expectedMin - weekMin;
    const anchorSub = !contractMin ? 'this week'
      : shortMin > 0
        ? `${fmtBig(shortMin)} short of ${fmtBig(expectedMin)} expected`
        : `of ${fmtBig(expectedMin)} expected`;

    const statusLabel = sheet.status === 'rejected' ? 'Sent back' : titleCase(sheet.status || 'draft');
    const statusPill = pill(statusLabel, STATUS_TONES[sheet.status]);

    const notices = `
      ${sheet.status === 'rejected' && sheet.reviewNote ? `<div class="alert-v4 wt-notice"><div class="alert-text">
        <b>Sent back${sheet.reviewedBy ? ` by ${escapeHtml(sheet.reviewedBy)}` : ''}.</b> ${escapeHtml(sheet.reviewNote)}
        ${Array.isArray(sheet.reviewFlags) && sheet.reviewFlags.length ? `<br>Look at: ${escapeHtml(sheet.reviewFlags.map((f) => String(f).split('|')[0]).filter(Boolean).join(', '))}` : ''}
        <br>The week is open for you to fix and resubmit${sheet.unlocked ? ' (unlocked past the Tuesday deadline)' : ''}.</div></div>` : ''}
      ${timeLocked ? `<p class="wt-note">This week locked on ${escapeHtml(lockDateLbl)} at 10:00.
        ${sheet.unlockRequested ? '<b class="tk4-strong">Unlock requested</b> — waiting for a coordinator to open it.' : '<button type="button" class="btn sm" id="req-unlock">Request unlock</button>'}</p>` : ''}
      ${sheet.unlocked ? `<p class="wt-note">Unlocked${sheet.unlockedBy ? ' by ' + escapeHtml(sheet.unlockedBy) : ''} — make your changes and submit.</p>` : ''}
      ${locked ? '' : idleLine()}`;

    // Submit / Recall — desktop shows full buttons, mobile a text link.
    const canSubmit = (sheet.status === 'draft' || sheet.status === 'rejected') && !timeLocked;
    const submitDesktop = canSubmit
      ? '<button type="button" class="btn primary" id="submit">Submit week</button>'
      : sheet.status === 'submitted' ? '<button type="button" class="btn" id="recall">Recall to draft</button>' : '';
    const submitMobile = canSubmit
      ? '<button id="submit" style="background:none;border:none;padding:0;font-size:12px;color:var(--link);cursor:pointer;">Submit</button>'
      : sheet.status === 'submitted' ? '<button id="recall" style="background:none;border:none;padding:0;font-size:12px;color:var(--link);cursor:pointer;">Recall</button>' : '';

    const NAV_GLYPH = 'background:none;border:none;padding:0;cursor:pointer;font-family:var(--font-technical);font-size:14px;color:var(--ink-3);';
    const isThisWeek = isoDay(monday) === isoDay(mondayOf(new Date()));

    // v4 (p10): ‹ 19 – 25 October 2026 › [Draft] …… 26:00 of 30:00 expected · 4:00 short [Add project] [Submit week]
    const totalLine = !contractMin
      ? '<span class="wt-total"><b id="weektot">0:00</b> this week</span>'
      : `<span class="wt-total"><b id="weektot">0:00</b> of ${escapeHtml(fmtBig(expectedMin))} expected${shortMin > 0 ? ` · <span class="late">${escapeHtml(fmtBig(shortMin))} short</span>` : ''}</span>`;
    const desktopHead = `
      <div class="wt-bar">
        <div class="wt-bar-l">
          <button type="button" class="btn wt-step" id="wk-prev" aria-label="Previous week">‹</button>
          <span class="wt-range">${escapeHtml(rangeLabel(monday))}</span>
          <button type="button" class="btn wt-step" id="wk-next" aria-label="Next week">›</button>
          ${statusPill}
          ${isThisWeek ? '<button type="button" class="linkbtn wt-now" id="wk-now" hidden>This week</button>' : '<button type="button" class="linkbtn wt-now" id="wk-now">This week</button>'}
          ${weekendCtl}
          <span id="save-status" class="wt-save"></span>
        </div>
        <div class="wt-bar-r">
          ${totalLine}
          ${locked ? '' : '<button type="button" class="btn" id="add-proj">Add project</button>'}
          ${submitDesktop}
        </div>
      </div>
      ${notices}`;

    const mobileHead = `
      <div style="display:flex;align-items:flex-end;gap:16px;">
        <span id="weektot" style="font-size:52px;font-weight:500;line-height:0.86;letter-spacing:-0.04em;color:var(--anchor);">0:00</span>
        <div style="display:flex;flex-direction:column;gap:4px;padding-bottom:4px;">
          <span style="font-size:9.5px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;color:var(--brand-orange);">This week</span>
          <span style="font-size:12.5px;color:var(--ink-2);line-height:1.4;">${escapeHtml(anchorSub)}</span>
        </div>
      </div>
      <div style="margin-top:18px;display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
        ${statusPill}
        <button id="wk-prev" style="${NAV_GLYPH}">‹</button>
        <span style="font-size:12px;color:var(--ink-3);">${escapeHtml(weekLabel(monday))}</span>
        <button id="wk-next" style="${NAV_GLYPH}">›</button>
        <button id="wk-now" style="background:none;border:none;padding:0;cursor:pointer;font-size:12px;color:var(--link);">This week</button>
        <span id="save-status" style="font-size:11px;color:var(--ink-3);"></span>
        <div style="flex:1;"></div>
        ${submitMobile}
      </div>
      ${weekendCtl ? `<div style="margin-top:12px;">${weekendCtl}</div>` : ''}
      ${notices}`;

    mount.innerHTML = `
      <div class="ts4"${isMobile ? ' style="padding:20px 16px 28px;display:flex;flex-direction:column;"' : ''}>
        ${isMobile ? mobileHead : desktopHead}
        ${isMobile ? mobileGrid() : desktopGrid()}
      </div>`;

    statusEl = mount.querySelector('#save-status');
    paintConflict();

    // Truncated client names scroll to reveal their tail after 10 seconds
    // (Karan 07/07): every name travels at the SAME SPEED (so long names take
    // proportionally longer, not faster), holds at the end, returns, then
    // pauses 10 seconds before repeating. Web Animations API because each
    // name needs its own distance-derived timing.
    setTimeout(() => {
      const SPEED = 30;   // px per second — identical for every row
      const HOLD = 1.2;   // seconds parked at the far end so the tail is readable
      const PAUSE = 10;   // seconds at rest between cycles
      mount.querySelectorAll('.ts-proj-name').forEach((box) => {
        const inner = box.querySelector('.ts-proj-name-in');
        if (!inner) return;
        const dist = inner.scrollWidth - box.clientWidth + 8;
        if (dist <= 12) return; // fits (or as good as) — stay still
        const travel = dist / SPEED;
        const total = travel * 2 + HOLD + PAUSE;
        inner.animate([
          { transform: 'translateX(0)', offset: 0, easing: 'ease-in-out' },
          { transform: `translateX(${-dist}px)`, offset: travel / total },
          { transform: `translateX(${-dist}px)`, offset: (travel + HOLD) / total, easing: 'ease-in-out' },
          { transform: 'translateX(0)', offset: (travel * 2 + HOLD) / total },
          { transform: 'translateX(0)', offset: 1 },
        ], { duration: total * 1000, iterations: Infinity });
        inner.classList.add('scrolling'); // marker only (no CSS attached)
      });
    }, 10000);

    function recalc() {
      let week = 0;
      const dayTotals = Object.fromEntries(DAY_KEYS.map((k) => [k, 0]));
      sheet.rows.forEach((r, ri) => {
        let rowT = 0;
        DAY_KEYS.forEach((k) => { const m = parse(r.days && r.days[k]); rowT += m; dayTotals[k] += m; });
        week += rowT;
        const cell = mount.querySelector(`#rowtot-${ri}`);
        if (cell) { cell.textContent = fmt(rowT) || '0:00'; cell.classList.toggle('has', rowT > 0); }
      });
      DAY_KEYS.forEach((k) => {
        const c = mount.querySelector(`#daytot-${k}`);
        if (c) { c.textContent = fmt(dayTotals[k]) || (c.classList.contains('ts4-dt') ? '–' : '0:00'); c.classList.toggle('has', dayTotals[k] > 0); }
      });
      const wt = mount.querySelector('#weektot'); if (wt) wt.textContent = fmtBig(week);
      const wg = mount.querySelector('#weekgrand'); if (wg) wg.textContent = fmtBig(week);
      return week;
    }

    // One popup per project (Karan 13/07): click any day cell to log time,
    // add the project's comment, mark review, or add mileage / an expense.
    function openLogTime(ri, focusDay) {
      // DIALOGS&FORMS Q: edits the week already open (7a → not ideally a modal),
      // but it is a bespoke day-grid, not a field form. Left on openOverlay; a
      // panel variant is the follow-up, flagged rather than decided.
      const r = sheet.rows[ri];
      // A day-cell click shows ONLY that day (Karan 13/07). The project name
      // opens the whole week (focusDay omitted) for multi-day editing.
      const one = focusDay ? vdays.find((d) => d.key === focusDay) : null;
      const logDays = one ? [one] : vdays;
      const { content, close } = openOverlay({
        title: isAspTime(r) ? `${r.label} · practice time` : `${rowNumber(r)} · ${r.label}`,
        subtitle: one ? `Log time · ${one.label} ${one.num}` : (r.task || 'Log time'),
      });
      // §4a: instruction chip, an oversized hours field with quick-add chips
      // (single day) or a labelled field per day (whole week), the project
      // comment, the review toggle, then mileage / expense and the footer.
      const CAPS_LBL = 'font-size:10px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;color:var(--ink-3);';
      const CHIP = 'border:1px solid var(--hair);border-radius:999px;padding:8px 13px;font-size:12px;background:none;color:var(--ink);cursor:pointer;';
      const instrDesc = r.instructionDesc || (Number(r.instructionSeq) === 1 ? 'Original scope' : 'Instruction');
      const hoursBody = one
        ? `<div style="display:flex;align-items:center;gap:14px;flex-wrap:wrap;">
             <input class="ts-logday-in" data-dk="${one.key}" data-focus="1" value="${escapeHtml((r.days && r.days[one.key]) || '')}" placeholder="0:00" inputmode="numeric"
               style="border:1px solid var(--ink);border-radius:8px;padding:14px 18px;font-family:var(--font-technical);font-size:24px;font-weight:500;letter-spacing:-0.02em;width:116px;box-sizing:border-box;color:var(--ink);background:none;">
             <div style="display:flex;gap:7px;flex-wrap:wrap;">
               <button type="button" data-add="15" style="${CHIP}">+0:15</button>
               <button type="button" data-add="30" style="${CHIP}">+0:30</button>
               <button type="button" data-add="60" style="${CHIP}">+1:00</button>
               <button type="button" data-set="450" style="${CHIP}">7:30</button>
             </div>
             <div style="flex:1;"></div>
             <button type="button" id="lt-week" style="background:none;border:none;padding:0;font-size:12.5px;color:var(--link);cursor:pointer;">Log the whole week</button>
           </div>`
        : `<div style="display:flex;gap:10px;flex-wrap:wrap;">
             ${logDays.map((d) => `<div style="display:flex;flex-direction:column;gap:6px;">
               <span style="font-size:11px;color:var(--ink-3);">${escapeHtml(d.label)} ${escapeHtml(d.num)}</span>
               <input class="ts-logday-in" data-dk="${d.key}"${focusDay === d.key ? ' data-focus="1"' : ''} value="${escapeHtml((r.days && r.days[d.key]) || '')}" placeholder="0:00" inputmode="numeric"
                 style="border:1px solid var(--hair);border-radius:8px;padding:10px 12px;font-family:var(--font-technical);font-size:15px;font-weight:500;width:82px;box-sizing:border-box;color:var(--ink);background:none;"></div>`).join('')}
           </div>`;
      content.innerHTML = `<div style="padding:24px 26px 26px;display:flex;flex-direction:column;gap:22px;">
        ${Number(r.instructionSeq) ? `<div style="display:flex;align-items:center;gap:14px;padding:13px 15px;background:var(--brief-bg);border-radius:10px;">
          <span style="font-family:var(--font-technical);font-size:15px;font-weight:500;">#${escapeHtml(String(r.instructionSeq))}</span>
          <span style="flex:1;min-width:0;font-size:13.5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(instrDesc)}</span>
        </div>` : ''}
        <div style="display:flex;flex-direction:column;gap:9px;">
          <span style="${CAPS_LBL}">${one ? `Hours — ${escapeHtml(one.label)} ${escapeHtml(one.num)}` : 'Hours per day'}</span>
          ${hoursBody}
        </div>
        <div style="display:flex;flex-direction:column;gap:7px;">
          <span style="${CAPS_LBL}">Comment <span style="letter-spacing:0;text-transform:none;font-weight:400;color:var(--ink-3);">${one ? 'this day' : 'this project'}</span></span>
          <textarea id="lt-note" rows="3" placeholder="${one ? 'What was done on this day…' : 'What was done on this project…'}"
            style="border:1px solid var(--hair);border-radius:8px;padding:12px 14px;font-size:13.5px;line-height:1.5;color:var(--ink-2);min-height:66px;resize:vertical;font-family:inherit;box-sizing:border-box;">${escapeHtml(one ? ((r.dayNotes || {})[one.key] || r.note || '') : (r.note || ''))}</textarea>
        </div>
        ${isAspTime(r) ? '' : `<label style="display:flex;align-items:flex-start;gap:12px;cursor:pointer;">
          <input type="checkbox" id="lt-rev"${r.review ? ' checked' : ''} style="width:18px;height:18px;margin:1px 0 0;flex:0 0 18px;">
          <span style="font-size:13.5px;line-height:1.5;color:var(--ink-2);">This is <span style="font-weight:500;color:var(--ink);">review</span> time — marks the project Ready to Issue when the sheet is approved</span>
        </label>`}
        <label style="display:flex;align-items:flex-start;gap:12px;cursor:pointer;">
          <input type="checkbox" id="lt-pin"${isPinned(r) ? ' checked' : ''} style="width:18px;height:18px;margin:1px 0 0;flex:0 0 18px;">
          <span style="font-size:13.5px;line-height:1.5;color:var(--ink-2);"><span style="font-weight:500;color:var(--ink);">Keep on my timesheet</span> — never taken off for having no time for ${IDLE_WEEKS} weeks</span>
        </label>
        ${one && mileageRate() && mileageOn ? '<div id="lt-mi"></div>' : ''}
        <div style="display:flex;gap:11px;align-items:center;flex-wrap:wrap;padding-top:20px;border-top:1px solid var(--hair);">
          ${(one && mileageRate()) || !mileageOn ? '' : '<button id="lt-mileage" type="button" style="border:1px solid var(--hair);border-radius:8px;padding:10px 17px;font-size:13px;background:none;color:var(--ink);cursor:pointer;">Add mileage</button>'}
          ${isAspTime(r) ? '' : `<button id="lt-expense" type="button" style="border:1px solid var(--hair);border-radius:8px;padding:10px 17px;font-size:13px;background:none;color:var(--ink);cursor:pointer;">Add expense</button>`}
          <div style="flex:1;"></div>
          <span style="font-size:12px;color:var(--ink-3);">${isAspTime(r) ? 'Mileage for practice time goes to Finance with no project' : (one && mileageRate() ? `An expense attaches to ${escapeHtml(r.number)} and goes to Finance` : `Both attach to ${escapeHtml(r.number)} and go to Finance`)}</span>
        </div>
        <div style="display:flex;align-items:center;gap:11px;padding-top:18px;border-top:1px solid var(--hair);">
          <button id="lt-cancel" type="button" style="border:1px solid var(--hair);border-radius:8px;padding:10px 17px;font-size:13px;background:none;color:var(--ink);cursor:pointer;">Cancel</button>
          <div id="lt-foot" class="tf-note" style="flex:1;"></div>
          <button id="lt-save" type="button" style="background:var(--ink-fill);color:var(--ink-fill-ink);border:none;border-radius:8px;padding:10px 19px;font-size:13px;font-weight:500;cursor:pointer;">Save</button>
        </div>
      </div>`;
      // h:mm only — a full stop becomes the colon as you type (Karan 25/08).
      content.querySelectorAll('.ts-logday-in').forEach(bindTimeInput);
      (content.querySelector('[data-focus="1"]') || content.querySelector('.ts-logday-in'))?.focus();
      // Quick-add chips (single-day view) bump or set the one field; the link
      // re-opens the same project across the whole week.
      if (one) {
        const inp = content.querySelector('.ts-logday-in');
        content.querySelectorAll('[data-add],[data-set]').forEach((c) => c.addEventListener('click', () => {
          inp.value = fmtBig(c.dataset.set ? Number(c.dataset.set) : parse(inp.value) + Number(c.dataset.add));
          inp.focus();
        }));
        content.querySelector('#lt-week')?.addEventListener('click', () => { close(); openLogTime(ri); });
      }
      content.querySelector('#lt-cancel').addEventListener('click', close);
      // MILEAGE INSIDE LOG TIME (build pack 26/10/2026): a day's popup carries the
      // tick box and the block — no second dialog; the day is this day. The
      // whole-week view has no single day, so it keeps the standalone dialog.
      const saveBtn = content.querySelector('#lt-save');
      const footEl = content.querySelector('#lt-foot');
      let mb = null; let timeSaved = false;
      const paintSave = () => {
        if (!mb) return;
        const v = mb.view(); const claim = mb.claim();
        saveBtn.textContent = timeSaved ? (claim ? `Save ${fmtMiles(claim.miles)} mi` : 'Done') : `Save${claim ? ` + ${fmtMiles(claim.miles)} mi` : ''}`;
        saveBtn.disabled = !v.canSave;
        saveBtn.style.opacity = v.canSave ? '' : '0.45';
        saveBtn.style.cursor = v.canSave ? 'pointer' : 'not-allowed';
        if (!footEl.dataset.err) {
          if (v.ticked && v.phase === 'working') footEl.replaceChildren(footNote('Waiting for the route'));
          else if (v.ticked && !v.canSave) footEl.replaceChildren(footNote('Enter the miles, or untick mileage', { busy: false }));
          else footEl.replaceChildren();
        }
      };
      const miHost = content.querySelector('#lt-mi');
      if (miHost) {
        const proj = isAspTime(r) ? null : (projects.find((p) => p.id === r.projectId) || { id: r.projectId, number: r.number, clientName: r.label });
        mb = mileageBlock({
          tickLabel: isAspTime(r) ? 'Add mileage for this day' : 'Add mileage for this site visit',
          onChange: paintSave,
          onAddAddress: (p) => { close(); document.dispatchEvent(new CustomEvent('track:open-project', { detail: { projectId: p.id, projectNo: p.number } })); },
        });
        miHost.style.cssText = 'margin-top:-6px;';
        miHost.appendChild(mb.el);
        mb.setProject(proj);
      }
      content.querySelector('#lt-mileage')?.addEventListener('click', () =>
        expenseForm(isAspTime(r) ? { session, type: 'Mileage' } : { session, project: { ...r, projectId: r.projectId }, type: 'Mileage' }));
      // Opened from a timesheet row: the project is filled in and "Project
      // Expenses" is SUGGESTED, not fixed (Karan 26/10/2026).
      content.querySelector('#lt-expense')?.addEventListener('click', () =>
        expenseForm({ session, project: { ...r, projectId: r.projectId }, type: 'Expense', suggestedCategory: 'Project Expenses' }));
      saveBtn.addEventListener('click', async () => {
        if (saveBtn.disabled) return;
        const claim = mb ? mb.claim() : null;
        if (timeSaved) {   // the time went in; this is Try again for the mileage
          try { await saveMileage(claim); close(); } catch (e) { mileageFailed(e); }
          return;
        }
        r.days = r.days || {};
        content.querySelectorAll('.ts-logday-in').forEach((inp) => {
          // Save what the field settles to, in case Save was clicked without blurring.
          const v = normaliseTime(inp.value);
          if (v) r.days[inp.dataset.dk] = v; else delete r.days[inp.dataset.dk];
        });
        const noteVal = content.querySelector('#lt-note').value.trim();
        if (one) { r.dayNotes = { ...(r.dayNotes || {}) }; if (noteVal) r.dayNotes[one.key] = noteVal; else delete r.dayNotes[one.key]; }
        else r.note = noteVal;
        r.review = !!content.querySelector('#lt-rev')?.checked;
        const pinBox = content.querySelector('#lt-pin');
        if (pinBox && pinBox.checked !== isPinned(r)) setPinned(r, pinBox.checked);
        // One save writes the time entry, then the mileage claim. If the time
        // saves and the mileage doesn't, stay open on the mileage block.
        scheduleSave(); timeSaved = true;
        if (!claim) { close(); draw(); return; }
        content.querySelectorAll('.ts-logday-in, #lt-note, #lt-rev, #lt-pin, [data-add], [data-set]').forEach((n) => { n.disabled = true; });
        try { await saveMileage(claim); close(); draw(); } catch (e) { draw(); mileageFailed(e); }
      });
      async function saveMileage(claim) {
        if (!claim) return;
        const proj = isAspTime(r) ? null : (projects.find((p) => p.id === r.projectId) || { id: r.projectId, number: r.number, clientName: r.label });
        saveBtn.disabled = true; saveBtn.textContent = 'Saving…';
        await addExpense(mileageExpense({ session, ownerFields, date: isoDay(one.date), project: proj, claim, rate: mileageRate() }));
        expenseFlags[r.projectId] = { ...(expenseFlags[r.projectId] || {}), mileage: true };
      }
      function mileageFailed(e) {
        console.warn('Log time: mileage did not save', e);
        footEl.dataset.err = '1';
        footEl.style.color = 'var(--brand-orange)';
        footEl.textContent = 'Time logged. Mileage didn’t save — Try again.';
        paintSave();
      }
    }

    mount.querySelectorAll('[data-rm]').forEach((b) => b.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const r = sheet.rows[+b.dataset.rm];
      if (r && r.projectId) { hidden.add(r.projectId); saveHidden(); } // stays removed next week
      if (r && isPinned(r)) setPinned(r, false);                        // crossing off un-pins it
      sheet.rows.splice(+b.dataset.rm, 1); draw(); scheduleSave();
    }));
    mount.querySelectorAll('[data-pin]').forEach((b) => b.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const r = sheet.rows[+b.dataset.pin]; if (!r) return;
      setPinned(r, !isPinned(r)); draw();
    }));
    // The idle notice: Undo puts every row it took off back on this week, pinned
    // (so it is not taken off again); Dismiss hides the line for this week.
    mount.querySelector('#ts-idle-undo')?.addEventListener('click', () => {
      const back = idleShown();
      back.forEach((x) => { const row = restoreRow(x); sheet.rows.push(row); setPinned(row, true); });
      const keys = new Set(back.map((x) => x.key));
      sheet.idleRemoved = (sheet.idleRemoved || []).filter((x) => !keys.has(x.key));
      draw(); scheduleSave();
    });
    mount.querySelector('#ts-idle-x')?.addEventListener('click', () => {
      try { localStorage.setItem(noticeKey(), idleShown().map((x) => x.key).join(',')); } catch (e) { /* ignore */ }
      mount.querySelector('#ts-idle')?.remove();
    });
    mount.querySelectorAll('[data-open]').forEach((el) => {
      const go = () => openLogTime(+el.dataset.open, el.dataset.day);
      el.addEventListener('click', go);
      el.addEventListener('keydown', (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); go(); } });
    });
    mount.querySelectorAll('[data-openweek]').forEach((el) => {
      const go = () => openLogTime(+el.dataset.openweek); // no focusDay → whole week
      el.addEventListener('click', go);
      el.addEventListener('keydown', (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); go(); } });
    });
    // Mobile day picker: choosing a day re-draws that day's rows.
    mount.querySelectorAll('[data-mday]').forEach((b) => b.addEventListener('click', () => { mobileDay = b.dataset.mday; draw(); }));

    mount.querySelector('#wk-prev').addEventListener('click', () => { monday.setDate(monday.getDate() - 7); loadWeek(); });
    mount.querySelector('#wk-next').addEventListener('click', () => { monday.setDate(monday.getDate() + 7); loadWeek(); });
    mount.querySelector('#wk-now').addEventListener('click', () => { monday = mondayOf(new Date()); loadWeek(); });
    const addBtn = mount.querySelector('#add-proj');
    if (addBtn && !locked) addBtn.addEventListener('click', addProjectPicker);
    const submitBtn = mount.querySelector('#submit');
    if (submitBtn) submitBtn.addEventListener('click', async () => {
      // Contract-week check: flag anything under the expected week before it goes in.
      const total = recalc();
      const contractMin = weekContract(users, session);
      if (contractMin && total < contractMin && submitBtn.dataset.confirmed !== '1') {
        submitBtn.dataset.confirmed = '1';
        submitBtn.textContent = 'Submit anyway';
        statusEl.textContent = `Week total ${fmtBig(total)} is under the ${contractLabel(contractMin)} contract week — check before submitting.`;
        statusEl.classList.add('error');
        return;
      }
      submitBtn.disabled = true;
      try {
        sheet.status = 'submitted';
        sheet.submittedAt = new Date().toISOString();
        sheet.totalMinutes = recalc();
        if (!await saveNow()) { submitBtn.disabled = false; sheet.status = 'draft'; return; }
        draw();
      } catch (e) { submitBtn.disabled = false; statusEl.textContent = e.message || String(e); }
    });
    const recallBtn = mount.querySelector('#recall');
    if (recallBtn) recallBtn.addEventListener('click', async () => {
      sheet.status = 'draft';
      await saveNow().catch(() => {});
      draw();
    });
    const reqUnlockBtn = mount.querySelector('#req-unlock');
    if (reqUnlockBtn) reqUnlockBtn.addEventListener('click', async () => {
      reqUnlockBtn.disabled = true;
      sheet.unlockRequested = true;
      sheet.unlockRequestedAt = new Date().toISOString();
      await saveNow().catch((e) => alert(e.message || e));
      draw();
    });
    const reqWeekendBtn = mount.querySelector('#req-weekend');
    if (reqWeekendBtn) reqWeekendBtn.addEventListener('click', async () => {
      reqWeekendBtn.disabled = true;
      sheet.weekendRequested = true;
      await saveNow().catch((e) => alert(e.message || e));
      draw();
    });
    // §3c mobile footer: Mileage logs a (project-optional) trip; Log time picks
    // a project to add to the day, which then opens the per-project log popup.
    const mMileageBtn = mount.querySelector('#ts-m-mileage');
    if (mMileageBtn) mMileageBtn.addEventListener('click', () => expenseForm({ session, type: 'Mileage' }));
    const mLogBtn = mount.querySelector('#ts-m-log');
    if (mLogBtn && !locked) mLogBtn.addEventListener('click', addProjectPicker);

    recalc();
  }

  await loadWeek();
}
